/**
 * This file is responsible for providing the `WFDataModelHooks` context, which
 * is the primary interface for reading data from the weaveflow data model. The
 * primary exported symbols are:
 *  - useWFHooks: used by react components to get access to the weaveflow data
 *    model.
 *  - WFDataModelAutoProvider: automatically detects the engine that backs the
 *    project and configures the context accordingly.
 */

import React, {createContext, type FC, useContext, useMemo} from 'react';

import {useHasTraceServerClientContext} from './traceServerClientContext';
import {
  makeTraceServerEndpointHook,
  projectIdFromParts,
  tsWFDataModelHooks,
} from './tsDataModelHooks';
import {
  type CallFilter,
  type WFDataModelHooksInterface,
} from './wfDataModelHooksInterface';

const WFDataModelHooksContext = createContext<WFDataModelHooksInterface | null>(
  null
);

export const useWFHooks = () => {
  const ctx = useContext(WFDataModelHooksContext);
  if (ctx === null) {
    throw new Error('No WFDataModelHooksContext');
  }
  return ctx;
};

export const WFDataModelAutoProvider: FC<{children?: React.ReactNode}> = ({
  children,
}) => {
  return (
    <WFDataModelHooksContext.Provider value={tsWFDataModelHooks}>
      {children}
    </WFDataModelHooksContext.Provider>
  );
};

/**
 * Returns true if the client can connect to trace server and the project has
 * objects or calls.
 */
export const useProjectHasTraceServerData = (
  entity: string,
  project: string
) => {
  const hasTraceServer = useHasTraceServerClientContext();
  const objs = tsWFDataModelHooks.useRootObjectVersions({
    entity,
    project,
    limit: 1,
    metadataOnly: true,
    skip: !hasTraceServer,
    noAutoRefresh: true,
  });
  const calls = tsWFDataModelHooks.useProjectHasCalls({
    entity,
    project,
    skip: !hasTraceServer,
  });
  const loading = objs.loading || calls.loading;
  return useMemo(
    () => ({
      loading,
      result: (objs.result ?? []).length > 0 || (calls.result ?? false),
    }),
    [loading, objs.result, calls.result]
  );
};

/**
 * Non-bucketed (scalar) all-time rollup that counts every span. `count` over a
 * field counts all rows (field validity is always true), so the single row's
 * count is the project's all-time span total.
 */
const useProjectAgentSpanCountEndpoint = makeTraceServerEndpointHook<
  'agentSpansStats',
  {entity: string; project: string; skip?: boolean},
  number
>(
  'agentSpansStats',
  params => {
    // All-time single-bucket rollup: time buckets anchor to the epoch origin,
    // so [epoch, now] with granularity = (now - epoch) collapses to one bucket.
    const now = Date.now();
    return {
      params: {
        project_id: projectIdFromParts({
          entity: params.entity,
          project: params.project,
        }),
        start: new Date(0).toISOString(),
        end: new Date(now).toISOString(),
        granularity: Math.ceil(now / 1000),
        metrics: [
          {
            alias: 'spans',
            value_type: 'datetime',
            value: {source: 'field', key: 'started_at'},
            aggregations: ['count'],
          },
        ],
      },
      skip: params.skip,
    };
  },
  res => {
    const row = res.rows[0];
    return row ? Number(row.count_spans ?? 0) : 0;
  }
);

/**
 * All-time agent span count for a project. Agent spans live in a separate table
 * from calls/objects, so they aren't reflected in useProjectHasTraceServerData
 * or call stats. Skipped when `skip` is set or there is no trace server (e.g.
 * models-only on-prem deployments).
 */
export const useProjectAgentSpanCount = (params: {
  entity: string;
  project: string;
  skip?: boolean;
}) => {
  const hasTraceServer = useHasTraceServerClientContext();
  return useProjectAgentSpanCountEndpoint({
    ...params,
    skip: params.skip || !hasTraceServer,
  });
};

/**
 * Returns true if the project has any agent spans. Pass `skip` (e.g. when the
 * agents feature is disabled) to avoid the request.
 */
export const useProjectHasAgentData = (params: {
  entity: string;
  project: string;
  skip?: boolean;
}) => {
  const {result, loading, error} = useProjectAgentSpanCount(params);
  return useMemo(
    () => ({loading, error, result: (result ?? 0) > 0}),
    [loading, error, result]
  );
};

/**
 * Returns trace count for a Run if the client can connect to trace server.
 */
export const useRunTraceStats = (
  entity: string,
  project: string,
  runName: string
) => {
  const filter: CallFilter = {
    runIds: [`${entity}/${project}/${runName}`],
    traceRootsOnly: true,
  };
  const hasTraceServer = useHasTraceServerClientContext();
  const callStats = tsWFDataModelHooks.useCallsStats({
    entity,
    project,
    filter,
    skip: !hasTraceServer,
  });
  return callStats;
};
